/* SSL cart offers — Agentcy for SickScience
   ssl-cart-offer: swaps the single serums for the set in one /cart/update.js call. If the set is already in the cart,
   it adds the set first (so nothing is removed if it fails), then lowers the replaced lines by one.
   ssl-cart-subscribe: puts the chosen recurring selling plan on an existing one-time line.
   ssl-cart-pair: adds a paired product on the same recurring selling plan.
   The last cart request of each action asks for the cart drawer section (and the cart JSON) in the same response,
   so the drawer re-renders right away. Older fallback (cart.js + cart:refresh + reload) only runs if that is missing. */
(() => {
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const CART_JSON = 'ssl-cart-json';

  const post = (path, body) =>
    fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

  const onCartPage = () => /\/cart\/?$/.test(window.location.pathname);

  /* Section ids the theme wants re-rendered with a cart change (the cart drawer registers itself here). */
  const bundledSections = (withCartJson) => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    if (withCartJson) sections.push(CART_JSON);
    return [...new Set(sections)].slice(0, 5);
  };

  const withSections = (body, withCartJson) => {
    if (onCartPage()) return body;
    const sections = bundledSections(withCartJson);
    if (!sections.length) return body;
    return { ...body, sections: sections.join(','), sections_url: window.location.pathname };
  };

  const readCartJson = (sections) => {
    const html = sections && sections[CART_JSON];
    const match = html && html.match(/<script[^>]*data-ssl-cart-json[^>]*>([\s\S]*?)<\/script>/);
    if (!match) return null;
    try {
      const cart = JSON.parse(match[1]);
      return cart && Array.isArray(cart.items) ? cart : null;
    } catch (e) {
      return null;
    }
  };

  const fire = (cart) => {
    ['cart:change', 'cart:updated'].forEach((name) => {
      document.documentElement.dispatchEvent(
        new CustomEvent(name, { bubbles: true, detail: { cart, baseEvent: 'variant:add' } })
      );
    });
  };

  /* json: response of the last cart call. Full cart for change/update, line items for add. */
  const refreshCart = async (el, json) => {
    if (onCartPage()) {
      window.location.reload();
      return;
    }
    const sections = json && json.sections;
    if (sections && Object.keys(sections).some((id) => id !== CART_JSON && sections[id])) {
      let cart = Array.isArray(json.items) && 'item_count' in json ? { ...json } : readCartJson(sections);
      if (!cart) cart = await fetch(`${root}cart.js`).then((r) => r.json());
      const drawerSections = { ...sections };
      delete drawerSections[CART_JSON];
      cart.sections = drawerSections;
      fire(cart);
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    setTimeout(() => { if (el.isConnected) window.location.reload(); }, 2500);
  };

  const setBusy = (btn, busy) => {
    if (!btn) return;
    btn.disabled = busy;
    if (busy) btn.setAttribute('aria-busy', 'true');
    else btn.removeAttribute('aria-busy');
  };

  if (!customElements.get('ssl-cart-offer')) {
    class SslCartOffer extends HTMLElement {
      connectedCallback() {
        this.btn = this.querySelector('[data-offer-btn]');
        this.err = this.querySelector('[data-offer-error]');
        this.btn?.addEventListener('click', () => this.apply());
      }

      async apply() {
        if (this.busy) return;
        this.busy = true;
        setBusy(this.btn, true);
        if (this.err) this.err.hidden = true;
        try {
          let updates = {};
          try { updates = JSON.parse(this.dataset.updates || '{}'); } catch (e) { updates = {}; }
          const hasUpdates = Object.keys(updates).length > 0;

          /* Fast path: when the set is not in the cart yet, add it and lower the replaced lines in one update call
             (one cart write instead of two). */
          if (hasUpdates) {
            let current = null;
            try { current = await fetch(`${root}cart.js`).then((r) => r.json()); } catch (e) { current = null; }
            const setInCart = !current || current.items.some((item) => String(item.variant_id) === String(this.dataset.add));
            if (!setInCart) {
              const one = await post('cart/update.js', withSections({ updates: { ...updates, [this.dataset.add]: 1 } }, false));
              if (one.ok) {
                await refreshCart(this, await one.json());
                return;
              }
            }
          }

          const addBody = { items: [{ id: Number(this.dataset.add), quantity: 1 }] };
          const add = await post('cart/add.js', hasUpdates ? addBody : withSections(addBody, true));
          if (!add.ok) throw new Error(`cart/add ${add.status}`);
          let last = await add.json();
          if (hasUpdates) {
            const up = await post('cart/update.js', withSections({ updates }, false));
            if (!up.ok) throw new Error(`cart/update ${up.status}`);
            last = await up.json();
          }
          await refreshCart(this, last);
        } catch (err) {
          this.busy = false;
          setBusy(this.btn, false);
          if (this.err) this.err.hidden = false;
        }
      }
    }
    customElements.define('ssl-cart-offer', SslCartOffer);
  }

  if (!customElements.get('ssl-cart-subscribe')) {
    class SslCartSubscribe extends HTMLElement {
      connectedCallback() {
        this.btn = this.querySelector('[data-sub-btn]');
        this.err = this.querySelector('[data-sub-error]');
        if (!this.querySelector('input[name="ssl-sub-freq"]:checked')) {
          const first = this.querySelector('input[name="ssl-sub-freq"]');
          if (first) first.checked = true;
        }
        this.btn?.addEventListener('click', () => this.apply());
      }

      async apply() {
        const picked = this.querySelector('input[name="ssl-sub-freq"]:checked');
        if (!picked || this.busy) return;
        this.busy = true;
        setBusy(this.btn, true);
        if (this.err) this.err.hidden = true;
        try {
          const res = await post('cart/change.js', withSections({
            id: this.dataset.key,
            quantity: Number(this.dataset.qty) || 1,
            selling_plan: Number(picked.value),
          }, false));
          if (!res.ok) throw new Error(`cart/change ${res.status}`);
          await refreshCart(this, await res.json());
        } catch (err) {
          this.busy = false;
          setBusy(this.btn, false);
          if (this.err) this.err.hidden = false;
        }
      }
    }
    customElements.define('ssl-cart-subscribe', SslCartSubscribe);
  }

  if (!customElements.get('ssl-cart-pair')) {
    class SslCartPair extends HTMLElement {
      connectedCallback() {
        this.btn = this.querySelector('[data-pair-btn]');
        this.err = this.querySelector('[data-pair-error]');
        this.btn?.addEventListener('click', () => this.apply());
      }

      async apply() {
        if (this.busy) return;
        this.busy = true;
        setBusy(this.btn, true);
        if (this.err) this.err.hidden = true;
        try {
          const res = await post('cart/add.js', withSections({
            items: [{ id: Number(this.dataset.variant), quantity: 1, selling_plan: Number(this.dataset.plan) }],
          }, true));
          if (!res.ok) throw new Error(`cart/add ${res.status}`);
          await refreshCart(this, await res.json());
        } catch (err) {
          this.busy = false;
          setBusy(this.btn, false);
          if (this.err) this.err.hidden = false;
        }
      }
    }
    customElements.define('ssl-cart-pair', SslCartPair);
  }
})();
